import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useToast } from '../../../Hooks/ToasContext';
import { ICustomerEntity } from '../../../Services/api/customers/customers.interface';
import { ListCustomersUseCase } from '../../../Usecase/Customers/list-customers-usecase';
import api from '../../../Services/api';
import { useNavigate } from 'react-router-dom';

interface ManualOrderFormData {
  omieCompanyId: string;
  customerId: string;
}

interface OmieCompany {
  id: string;
  name: string;
  alias?: string;
  active: boolean;
  canIssueInvoice: boolean;
}

export interface ManualOrderProductData {
  productId: string;
  productMultiVariantId?: string;
  quantity: number;
  unitPrice: string;
}

interface ManualOrderSelectionData {
  addressId: string;
  products: ManualOrderProductData[];
  carrierId: string;
  shippingCost: string;
  paymentPreference: {
    type: 'PIX' | 'TICKET';
    installmentsInDays: number[];
  };
}

interface UseCreateManualOrderProps {
  listCustomersUseCase: ListCustomersUseCase;
}

export function useCreateManualOrder({
  listCustomersUseCase,
}: UseCreateManualOrderProps) {
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<ICustomerEntity[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<ICustomerEntity>();

  const { showToast } = useToast();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
  } = useForm<ManualOrderFormData>({
    defaultValues: {
      omieCompanyId: '',
      customerId: '',
    },
  });

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const response = await api.get('/backoffice/omie-companies');
        const responseData = response.data;
        const companies: OmieCompany[] = Array.isArray(responseData)
          ? responseData
          : responseData?.data ?? responseData?.items ?? responseData?.results ?? [];

        setCompanyOptions(companies
          .filter((company) => company.active && company.canIssueInvoice)
          .map((company) => ({
            label: company.alias
              ? `${company.name} (${company.alias})`
              : company.name,
            value: company.id,
          })));
      } catch {
        setCompanyOptions([]);
        showToast('Não foi possível carregar as empresas.', 'error');
      } finally {
        setCompaniesLoading(false);
      }
    };

    void loadCompanies();
  }, [showToast]);

  useEffect(() => {
    // Evita pesquisar enquanto o usuário digitou muito pouco.
    if (customerSearch.trim().length < 2) {
      setCustomers([]);
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        setLoading(true);

        const result = await listCustomersUseCase.handle({
          page: 1,
          searchBy: customerSearch.trim(),
        });

        if (result.isFailure) {
          showToast('Não foi possível pesquisar os clientes.', 'error');
          return;
        }

        setCustomers(result.getValue()?.data ?? []);
      } catch {
        showToast('Não foi possível pesquisar os clientes.', 'error');
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [customerSearch, listCustomersUseCase, showToast]);

  const handleSelectCustomer = async (customer: ICustomerEntity) => {
    try {
      setLoading(true);
      const response = await api.get<ICustomerEntity>(`/customers/${customer.id}`);
      const detailedCustomer = response.data;

      setSelectedCustomer(detailedCustomer);
      setValue('customerId', detailedCustomer.id, {
        shouldValidate: true,
        shouldDirty: true,
      });

      return detailedCustomer;
    } catch {
      showToast('Não foi possível carregar os dados completos do cliente.', 'error');
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (
    values: ManualOrderFormData,
    selection: ManualOrderSelectionData,
  ) => {
    if (!values.omieCompanyId) {
      showToast('Selecione a empresa.', 'error');
      return;
    }

    if (!selectedCustomer) {
      showToast('Selecione um cliente.', 'error');
      return;
    }

    try {
      setLoading(true);

      await api.post('/manual-orders', {
        omieCompanyId: values.omieCompanyId,
        customerId: selectedCustomer.id,
        addressId: selection.addressId,
        items: selection.products,
        carrierId: selection.carrierId,
        shippingCost: selection.shippingCost,
        paymentType: selection.paymentPreference.type,
        installmentsInDays: selection.paymentPreference.installmentsInDays,
      });

      showToast('Pedido manual criado com sucesso.', 'success');
      navigate('/manual-orders-list');
    } catch {
      showToast('Não foi possível criar o pedido manual.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return {
    control,
    handleSubmit,
    handleAction,
    loading,
    companiesLoading,
    companyOptions,
    customers,
    customerSearch,
    setCustomerSearch,
    selectedCustomer,
    handleSelectCustomer,
  };
}
